import { useState } from "react";

const ITEMS = [
  {
    id: "MAPA",
    description: "Complete MArriage and PArenting Program",
    sub: "2 manuals, 20 journals and all materials",
    code: "MAPA",
    price: 545,
  },
  {
    id: "MJOU",
    description: "MA & PA Journal (bound) / MA & PA Journal (unbound)",
    note: "FOR RE-ORDERS ONLY",
    code: "MJOU",
    price: 15,
  },
  {
    id: "MFAC",
    description: "Facilitator Manual",
    code: "MFAC",
    price: 70,
  },
  {
    id: "MCER",
    description: "Confidentiality Agreements",
    sub: "Program Evaluation Forms · Certificates of Achievement",
    note: "Pkg. of 20 ea.",
    code: "MCER",
    price: 25,
  },
  {
    id: "MPOS",
    description: "Posters (set of 12)",
    code: "MPOS",
    price: 98,
  },
];

function calcShipping(subtotal: number): number {
  if (subtotal <= 0) return 0;
  if (subtotal <= 15) return 4;
  if (subtotal <= 100) return 9;
  return subtotal * 0.09;
}

export default function OrderForm() {
  const [qtys, setQtys] = useState<Record<string, number>>(
    Object.fromEntries(ITEMS.map((i) => [i.id, 0]))
  );
  const [flTax, setFlTax] = useState(true);
  const [payment, setPayment] = useState<"check" | "visa" | "mastercard" | "amex">("check");
  const [ship, setShip] = useState<"business" | "residential">("business");
  const [form, setForm] = useState({
    name: "",
    agency: "",
    address: "",
    city: "",
    phone: "",
    email: "",
    cardNumber: "",
    expDate: "",
    signature: "",
    date: "",
  });

  const subtotal = ITEMS.reduce((sum, item) => sum + item.price * (qtys[item.id] ?? 0), 0);
  const salesTax = flTax ? subtotal * 0.07 : 0;
  const shipping = calcShipping(subtotal);
  const total = subtotal + salesTax + shipping;

  const fmt = (n: number) => (n > 0 ? `$${n.toFixed(2)}` : "—");

  function setQty(id: string, val: string) {
    const n = Math.max(0, parseInt(val) || 0);
    setQtys((prev) => ({ ...prev, [id]: n }));
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="bg-white border border-border rounded-2xl px-11 py-10 max-[680px]:px-6 max-[680px]:py-7">
        <h1 className="font-display text-[30px] font-semibold text-primary-dark mb-2.5 leading-tight">Order Form</h1>
        <p className="text-sm text-[#555] leading-relaxed max-w-[620px]">
          Click <a href="marriageandparenting/src/assets/MA&PA-order-form.pdf" className="text-primary font-medium underline">here</a> for the legacy form which maybe e-mailed, mailed or faxed to us.
        </p>
      </div>

      {/* Items Table + Shipping Info */}
      <div className="bg-white border border-border rounded-2xl overflow-hidden">
        <div className="bg-gradient-to-br from-primary to-primary-dark px-7 py-5">
          <p className="font-display text-[15px] text-white/75 leading-snug">Please select the item, noting the quantity and amount, then phone, fax or mail your order to us.</p>
        </div>
        <div className="px-8 py-6 max-[680px]:px-4 overflow-x-auto">
          <table className="w-full text-[13px] text-[#333] border-collapse">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left font-semibold py-2 pr-3 w-16">Qty.</th>
                <th className="text-left font-semibold py-2 pr-3">Description</th>
                <th className="text-left font-semibold py-2 pr-3 whitespace-nowrap">Item Code</th>
                <th className="text-right font-semibold py-2 pr-3">Price</th>
                <th className="text-right font-semibold py-2">Amount</th>
              </tr>
            </thead>
            <tbody>
              {ITEMS.map((item) => (
                <tr key={item.id} className="border-b border-border/60 last:border-0">
                  <td className="py-3 pr-3 align-top">
                    <input
                      type="number"
                      min={0}
                      value={(qtys[item.id] ?? 0) === 0 ? "" : (qtys[item.id] ?? 0)}
                      onChange={(e) => setQty(item.id, e.target.value)}
                      placeholder="0"
                      className="w-14 border border-border rounded-md px-2 py-1 text-center text-[13px] focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                  </td>
                  <td className="py-3 pr-3 align-top leading-relaxed">
                    <div className="font-semibold">{item.description}</div>
                    {item.sub && <div className="text-[12px] text-[#777]">{item.sub}</div>}
                    {item.note && (
                      <div className="text-[11px] font-light italic text-[#aaa] inline-block">
                        {item.note}
                      </div>
                    )}
                  </td>
                  <td className="py-3 pr-3 align-top font-mono text-[12px] whitespace-nowrap">{item.code}</td>
                  <td className="py-3 pr-3 align-top text-right whitespace-nowrap">${item.price}</td>
                  <td className="py-3 align-top text-right whitespace-nowrap">
                    {(qtys[item.id] ?? 0) > 0 ? `$${(item.price * (qtys[item.id] ?? 0)).toFixed(2)}` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="border-t border-border px-8 py-6 max-[680px]:px-4 grid grid-cols-[1fr_auto] gap-8 text-[13px] text-[#444] leading-relaxed max-[600px]:grid-cols-1">
          {/* Shipping Info */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 max-[500px]:grid-cols-1">
            <div>
              <div className="font-semibold text-[#222] mb-1">Shipping &amp; Handling:</div>
              <div className="font-medium text-[#333] mb-0.5">Continental U.S.</div>
              <div>Orders $15 and under, add $4.</div>
              <div>Orders $15–$100, add $9.</div>
              <div>Orders over $100, add 9%.</div>
            </div>
            <div>
              <div className="font-semibold text-[#222] mb-1">Foreign Orders:</div>
              <div>Actual Shipping Cost plus 10% Handling.</div>
            </div>
            <div>
              <div className="font-semibold text-[#222] mb-1">Special Shipping Requests:</div>
              <div>Costs of extra shipping charges (2nd Day Air, etc.) will be added to invoice.</div>
            </div>
            <div>
              <div className="font-semibold text-[#222] mb-1">Hawaii, Alaska, Canada:</div>
              <div>For orders under $100 add $18.</div>
              <div>For orders over $100, add 18%.</div>
            </div>
          </div>

          {/* Totals */}
          <div className="rounded-xl border border-border overflow-hidden self-start min-w-55">
            <div className="flex justify-between items-center px-4 py-3 border-b border-border bg-primary-mid">
              <span className="font-bold text-primary-light">Subtotal</span>
              <span className="font-medium text-primary-light">{fmt(subtotal)}</span>
            </div>
            <div className="flex justify-between items-center px-4 py-3 border-b border-border bg-primary-subtle">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={flTax}
                  onChange={(e) => setFlTax(e.target.checked)}
                  className="accent-primary"
                />
                FL only, 7% sales tax
              </label>
              <span className="font-medium">{flTax && subtotal > 0 ? fmt(salesTax) : "—"}</span>
            </div>
            <div className="flex justify-between items-center px-4 py-3 border-b border-border bg-primary-subtle">
              <span>Shipping &amp; Handling</span>
              <span className="font-medium">{fmt(shipping)}</span>
            </div>
            <div className="flex justify-between items-center px-4 py-3 bg-[#1a1a1a]">
              <span className="font-semibold text-white">Total</span>
              <span className="font-bold text-white">{fmt(total)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Payment + Ship To */}
      <div className="grid grid-cols-2 gap-4 max-[700px]:grid-cols-1">
        {/* Payment */}
        <div className="bg-white border border-border rounded-2xl px-9 py-8 text-[13px] text-[#444] max-[680px]:px-6">
          <div className="font-semibold text-[14px] text-[#222] mb-1">Please check method of payment:</div>
          <div className="text-[12px] text-[#888] mb-4">(All orders must be prepaid.)</div>

          <div className="flex flex-col gap-3 mb-5">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="payment" checked={payment === "visa"} onChange={() => setPayment("visa")} className="accent-primary" />
              {/* Visa Card SVG */}
              <svg xmlns="http://www.w3.org/2000/svg" className="w-9 h-6" viewBox="0 0 60 40" fill="none">
                <rect width="60" height="40" rx="5" fill="#1A1F71" />
                <text x="50%" y="57%" dominantBaseline="middle" textAnchor="middle" fill="white" fontSize="18" fontWeight="bold" fontFamily="Arial, sans-serif" letterSpacing="1" fontStyle="italic">VISA</text>
              </svg>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="payment" checked={payment === "mastercard"} onChange={() => setPayment("mastercard")} className="accent-primary" />
              {/* Mastercard SVG */}
              <svg xmlns="http://www.w3.org/2000/svg" className="w-9 h-6" viewBox="0 0 60 40" fill="none">
                <rect width="60" height="40" rx="5" fill="#f5f5f5" />
                <circle cx="23" cy="20" r="11" fill="#EB001B" />
                <circle cx="37" cy="20" r="11" fill="#F79E1B" />
                <path d="M30 11.5a11 11 0 0 1 0 17 11 11 0 0 1 0-17z" fill="#FF5F00" />
              </svg>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="payment" checked={payment === "amex"} onChange={() => setPayment("amex")} className="accent-primary" />
              {/* American Express SVG */}
              <svg xmlns="http://www.w3.org/2000/svg" className="w-9 h-6" viewBox="0 0 60 40" fill="none">
                <rect width="60" height="40" rx="5" fill="#2E77BC" />
                <text x="50%" y="57%" dominantBaseline="middle" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold" fontFamily="Arial, sans-serif" letterSpacing="0.5">AMERICAN</text>
                <text x="50%" y="78%" dominantBaseline="middle" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold" fontFamily="Arial, sans-serif" letterSpacing="0.5">EXPRESS</text>
              </svg>
            </label>
          </div>

          {payment !== "check" && (
            <div className="flex flex-col gap-3 pt-4 border-t border-border">
              <div className="flex flex-col gap-1">
                <label className="text-[12px] text-[#888]">Account #</label>
                <input
                  type="text"
                  value={form.cardNumber}
                  onChange={(e) => setForm((f) => ({ ...f, cardNumber: e.target.value }))}
                  className="border border-border rounded-md px-3 py-1.5 text-[13px] focus:outline-none focus:ring-1 focus:ring-primary"
                  placeholder="Card number"
                />
              </div>
              <div className="flex gap-3">
                <div className="flex flex-col gap-1 flex-1">
                  <label className="text-[12px] text-[#888]">Exp. Date</label>
                  <input
                    type="text"
                    value={form.expDate}
                    onChange={(e) => setForm((f) => ({ ...f, expDate: e.target.value }))}
                    className="border border-border rounded-md px-3 py-1.5 text-[13px] focus:outline-none focus:ring-1 focus:ring-primary"
                    placeholder="MM/YY"
                  />
                </div>
                <div className="flex flex-col gap-1 flex-1">
                  <label className="text-[12px] text-[#888]">Date</label>
                  <input
                    type="text"
                    value={form.date}
                    onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                    className="border border-border rounded-md px-3 py-1.5 text-[13px] focus:outline-none focus:ring-1 focus:ring-primary"
                    placeholder="Date"
                  />
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[12px] text-[#888]">Signature</label>
                <input
                  type="text"
                  value={form.signature}
                  onChange={(e) => setForm((f) => ({ ...f, signature: e.target.value }))}
                  className="border border-border rounded-md px-3 py-1.5 text-[13px] focus:outline-none focus:ring-1 focus:ring-primary"
                  placeholder="Authorized signature"
                />
              </div>
            </div>
          )}

          <p className="text-[11px] text-[#999] mt-5 leading-relaxed">
            All prices in U.S. currency. Prices subject to change without notice. All orders must be prepaid.
          </p>
        </div>

        {/* Ship To */}
        <div className="bg-white border border-border rounded-2xl px-9 py-8 text-[13px] text-[#444] max-[680px]:px-6">
          <div className="font-semibold text-[14px] text-[#222] mb-1">Ship to:</div>
          <div className="flex gap-4 mb-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="shipType" checked={ship === "business"} onChange={() => setShip("business")} className="accent-primary" />
              Business address
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="shipType" checked={ship === "residential"} onChange={() => setShip("residential")} className="accent-primary" />
              Residential address
            </label>
          </div>
          <p className="text-[11px] text-[#999] mb-5 leading-relaxed">
            Unless otherwise specified, all orders are shipped via UPS to street address. No PO Box please.
          </p>

          <div className="flex flex-col gap-3">
            {[
              { key: "name", label: "Name" },
              { key: "agency", label: "Agency" },
              { key: "address", label: "Address" },
              { key: "city", label: "City/State/ZIP" },
              { key: "phone", label: "Daytime phone" },
              { key: "email", label: "Email address" },
            ].map(({ key, label }) => (
              <div key={key} className="flex flex-col gap-1">
                <label className="text-[12px] text-[#888]">{label}</label>
                <input
                  type={key === "email" ? "email" : key === "phone" ? "tel" : "text"}
                  value={form[key as keyof typeof form]}
                  onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                  className="border border-border rounded-md px-3 py-1.5 text-[13px] focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Submit */}
      <div className="flex justify-end">
        <button
          className="bg-primary text-white font-medium text-[14px] px-8 py-3 rounded-xl hover:opacity-90 transition-opacity disabled:opacity-40"
          disabled={subtotal === 0}
        >
          Submit Order
        </button>
      </div>
    </div>
  );
}
